import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FileText,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowLeft,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
} from "lucide-react";
import {
  fetchUserDocuments,
  uploadPartnerDocument,
  fetchPartnerUserById,
} from "../../api/partner/partnerApi";
import {
  setUploadedDocuments,
  addUploadedDocument,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { showToast } from "../../components/common/notification/Notification";
import "./PartnerPages.css";

export const PartnerDocuments = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id;

  const [partnerDetails, setPartnerDetails] = useState(authUser?.user || null);
  const [documentsList, setDocumentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState("pan"); // 'pan' or 'rera'

  const loadVerificationData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [userRes, docsRes] = await Promise.allSettled([
        fetchPartnerUserById(token, userId),
        fetchUserDocuments(token),
      ]);

      if (userRes.status === "fulfilled" && userRes.value?.user) {
        setPartnerDetails(userRes.value.user);
      }

      if (docsRes.status === "fulfilled") {
        const docs = docsRes.value?.documents || docsRes.value?.data || [];
        setDocumentsList(docs);
        dispatch(setUploadedDocuments(docs));
      }
    } catch (err) {
      console.warn("Error loading document verification data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVerificationData();
  }, [token, userId]);

  const handleFileUpload = async (docKey, file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showToast("File size exceeds maximum limit of 10 MB.", { type: "error" });
      return;
    }

    setUploadingDocType(docKey);
    try {
      const response = await uploadPartnerDocument(token, userId, docKey, file);

      if (response?.statusType === "SUCCESS" || response?.status === "SUCCESS") {
        showToast(`${docKey.toUpperCase()} uploaded successfully!`, { type: "success" });
        dispatch(
          addUploadedDocument({
            name: docKey,
            document_type: docKey,
            file_url: response.document?.file_url || "",
          })
        );
        loadVerificationData();
      } else {
        const msg = response?.text || response?.message || "Document upload failed.";
        showToast(msg, { type: "error" });
      }
    } catch (err) {
      console.error("Document upload error:", err);
      showToast("Failed to upload document. Please try again.", { type: "error" });
    } finally {
      setUploadingDocType(null);
    }
  };

  const isVerified = Boolean(
    partnerDetails?.active_status ||
    partnerDetails?.is_verified ||
    partnerDetails?.status === "VERIFIED"
  );

  const hasPanUploaded = documentsList.some((d) => {
    const name = String(d.name || d.document_name || d.type || "").toLowerCase();
    return name.includes("pan");
  });

  const hasReraUploaded = documentsList.some((d) => {
    const name = String(d.name || d.document_name || d.type || "").toLowerCase();
    return name.includes("rera");
  });

  const hasAnyDocUploaded = hasPanUploaded || hasReraUploaded || documentsList.length > 0;

  const overallStatus = isVerified
    ? "VERIFIED"
    : hasAnyDocUploaded
    ? "UNDER_REVIEW"
    : "PENDING";

  return (
    <div className="partner-verification-page">
      <div className="partner-verification-page__container">
        {/* Navigation Breadcrumb */}
        <Link to="/partner/dashboard" className="partner-back-btn">
          <ArrowLeft size={16} />
          <span>Back to Partner Dashboard</span>
        </Link>

        {/* Overview Banner */}
        <div className="partner-verification-overview">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
            <div>
              <h1 className="partner-dash__title mb-1">Partner Document Verification</h1>
              <p className="partner-dash__subtitle">
                Your account requires one official verification document (PAN Card or RERA Certificate) before it can be activated by Super Admin.
              </p>
            </div>
            <button
              type="button"
              className="partner-btn partner-btn--outline d-flex align-items-center gap-2"
              onClick={loadVerificationData}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "partner-spin" : ""} />
              <span>{loading ? "Refreshing..." : "Refresh Status"}</span>
            </button>
          </div>

          <div className="d-flex align-items-center gap-3 pt-3 border-top">
            <span className="text-muted font-weight-bold" style={{ fontSize: "0.875rem" }}>
              Document Status:
            </span>
            {overallStatus === "VERIFIED" && (
              <span className="partner-chip partner-chip--success">
                <CheckCircle2 size={13} /> Document Verified ✓
              </span>
            )}
            {overallStatus === "UNDER_REVIEW" && (
              <span className="partner-chip partner-chip--info">
                <Clock size={13} /> Document Submitted ✓ (Under Review)
              </span>
            )}
            {overallStatus === "PENDING" && (
              <span className="partner-chip partner-chip--warning">
                <AlertCircle size={13} /> Verification Required
              </span>
            )}
          </div>
        </div>

        {/* Upload Card */}
        <div className="partner-card mb-4">
          <h3 className="partner-card__title mb-2">Complete Your Verification</h3>
          <p className="partner-card__subtitle mb-4">
            Upload either your business PAN Card or state RERA Certificate. Only one document is required for verification.
          </p>

          <div className="row g-4">
            {/* OPTION 1: PAN CARD */}
            <div className="col-12 col-md-6">
              <div className={`partner-doc-upload-item h-100 flex-column align-items-start ${hasPanUploaded ? "partner-doc-upload-item--review" : ""}`}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="partner-doc-upload-item__icon">
                    <FileText size={20} />
                  </div>
                  <div>
                    <h4 className="partner-doc-upload-item__title">Option A: PAN Card</h4>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>Business or Individual PAN</span>
                  </div>
                </div>

                <p className="partner-doc-upload-item__desc mb-3">
                  Upload a clear image or PDF scan of your Permanent Account Number card.
                </p>

                <div className="mt-auto w-100">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>Accepted: PDF, JPG, PNG (Max 10MB)</span>
                    {hasPanUploaded && <span className="partner-chip partner-chip--info">Uploaded</span>}
                  </div>
                  <label
                    htmlFor="doc-upload-pan"
                    className={`partner-btn w-100 ${hasPanUploaded ? "partner-btn--outline" : "partner-btn--primary"} ${uploadingDocType === "pan" ? "disabled" : ""}`}
                    style={{ cursor: uploadingDocType === "pan" ? "wait" : "pointer" }}
                  >
                    <UploadCloud size={16} />
                    <span>{uploadingDocType === "pan" ? "Uploading PAN..." : hasPanUploaded ? "Re-upload PAN Card" : "Upload PAN Card"}</span>
                  </label>
                  <input
                    id="doc-upload-pan"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="d-none"
                    disabled={uploadingDocType === "pan"}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload("pan", e.target.files[0]);
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* OPTION 2: RERA CERTIFICATE */}
            <div className="col-12 col-md-6">
              <div className={`partner-doc-upload-item h-100 flex-column align-items-start ${hasReraUploaded ? "partner-doc-upload-item--review" : ""}`}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="partner-doc-upload-item__icon">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h4 className="partner-doc-upload-item__title">Option B: RERA Certificate</h4>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>State Real Estate Authority Reg.</span>
                  </div>
                </div>

                <p className="partner-doc-upload-item__desc mb-3">
                  Upload your state RERA agent/promoter registration certificate.
                </p>

                <div className="mt-auto w-100">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>Accepted: PDF, JPG, PNG (Max 10MB)</span>
                    {hasReraUploaded && <span className="partner-chip partner-chip--info">Uploaded</span>}
                  </div>
                  <label
                    htmlFor="doc-upload-rera"
                    className={`partner-btn w-100 ${hasReraUploaded ? "partner-btn--outline" : "partner-btn--primary"} ${uploadingDocType === "rera" ? "disabled" : ""}`}
                    style={{ cursor: uploadingDocType === "rera" ? "wait" : "pointer" }}
                  >
                    <UploadCloud size={16} />
                    <span>{uploadingDocType === "rera" ? "Uploading RERA..." : hasReraUploaded ? "Re-upload RERA Certificate" : "Upload RERA Certificate"}</span>
                  </label>
                  <input
                    id="doc-upload-rera"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="d-none"
                    disabled={uploadingDocType === "rera"}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload("rera", e.target.files[0]);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 pt-3 border-top">
          <Link to="/partner/dashboard" className="partner-btn partner-btn--outline">
            <ArrowLeft size={16} />
            <span>Return to Partner Dashboard</span>
          </Link>
          <div className="text-muted" style={{ fontSize: "0.8125rem" }}>
            Documents are reviewed by the Super Admin team within 24 to 48 business hours.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerDocuments;
